export interface ISalaryComponent {
    id: string;
    institutionId: string;
    name: string;
    type: 'EARNING' | 'DEDUCTION';
    isActive: boolean;
    isOvertimeBase?: boolean;
    isOvertimeEarning?: boolean;
    isBonusEarning?: boolean;
    // T3b: read-only designation flag (see SalaryComponentDTO's javadoc for the write-side gap).
    isPieceRateEarning?: boolean;
    // Wage-composition role (garments gross split); at most one component per role.
    compositionRole?: CompositionRole;
}

export type CompositionRole = 'NONE' | 'HOUSE_RENT' | 'MEDICAL' | 'CONVEYANCE' | 'FOOD';

export interface IEmployeeSalaryDetail {
    id: string;
    salaryComponentId: string;
    componentName: string;
    componentType: string;
    amount: number;
}

export interface IEmployeeSalary {
    id: string;
    institutionId: string;
    userId: string;
    userName: string;
    employeeName?: string;
    effectiveFrom: string;
    isActive: boolean;
    details: IEmployeeSalaryDetail[];
    segmentId?: string;
    wageGradeId?: string;
    // H3/R7: resolved regardless of the grade's isActive, so an assignment to a now-inactive
    // grade still renders instead of silently vanishing from the form's Select.
    wageGradeDisplayName?: string;
    wageGradeActive?: boolean;
    // T3b: read side is always the resolved, persisted value (never UNSPECIFIED) -- see
    // IEmployeeSalaryCreate for the write-side tri-state contract.
    wageMode?: 'MONTHLY' | 'PIECE_RATE';
}

// T3b (N1): the wire-only sentinel WageMode#UNSPECIFIED -- posted whenever the operator
// deliberately chooses "Monthly" from the Select, so a deliberate reset is never confused with
// "didn't touch this field" (the direct analogue of CLEAR_WAGE_GRADE_SENTINEL below). NEVER a
// display value and NEVER persisted.
export const WAGE_MODE_UNSPECIFIED = 'UNSPECIFIED';

// R8/M9 (2026-09-08 wage-grades decision REV 2): wageGradeId is tri-state on write.
// - undefined -> CREATE: carry forward from the prior active salary (if any). UPDATE: no change.
// - CLEAR_WAGE_GRADE_SENTINEL -> explicit "No grade" choice; the salary is left/set unassigned.
// - any other id -> validated against the target institution and assigned.
// Mirrors EmployeeSalaryCreateDTO.CLEAR_WAGE_GRADE on the backend (the nil UUID).
export const CLEAR_WAGE_GRADE_SENTINEL = '00000000-0000-0000-0000-000000000000';

export interface IEmployeeSalaryCreate {
    institutionId: string;
    userId: string;
    effectiveFrom: string;
    details: {
        salaryComponentId: string;
        amount: number;
    }[];
    segmentId?: string;
    wageGradeId?: string;
    // T3b (N1): tri-state on write, mirroring wageGradeId immediately below.
    // - omitted -> CREATE: carry forward from the prior active salary (defaults to MONTHLY if
    //   none). UPDATE: no change. onChange must NEVER add this key on its own.
    // - WAGE_MODE_UNSPECIFIED -> the operator's explicit reset to Monthly.
    // - 'PIECE_RATE' -> assigned as given.
    wageMode?: 'MONTHLY' | 'PIECE_RATE' | 'UNSPECIFIED';
}

// Wage grade catalog (decision note 2026-09-08 REV 2). minimumBasicWage is deliberately not
// exposed (R3) -- v1 never surfaces or computes anything from it.
export interface IWageGrade {
    id: string;
    institutionId: string;
    code: string;
    displayName: string;
    minimumGrossWage: number;
    sortOrder: number;
    isActive: boolean;
    sourceNote?: string | null;
    effectiveFrom?: string | null;
    // C4/R4: null = manually created; otherwise the gazette value this row was seeded with.
    seededDefaultValue?: number | null;
    housePercentOfBasic?: number | null;
    medicalAllowance?: number | null;
    conveyanceAllowance?: number | null;
    foodAllowance?: number | null;
    annualIncrementPercent?: number | null;
}

export interface IWageGradeCreateRequest {
    institutionId: string;
    code: string;
    displayName: string;
    minimumGrossWage: number;
    sortOrder?: number;
    housePercentOfBasic?: number | null;
    medicalAllowance?: number | null;
    conveyanceAllowance?: number | null;
    foodAllowance?: number | null;
    annualIncrementPercent?: number | null;
}

export interface IWageGradeUpdateRequest {
    displayName: string;
    minimumGrossWage: number;
    sortOrder?: number;
    isActive: boolean;
    housePercentOfBasic?: number | null;
    medicalAllowance?: number | null;
    conveyanceAllowance?: number | null;
    foodAllowance?: number | null;
    annualIncrementPercent?: number | null;
}

export interface IPayrollSettings {
    id?: string;
    institutionId: string;
    deductForAbsence: boolean;
    workingDaysPerMonth: number;
    autoPostJournalEntry?: boolean;
    salaryExpenseAccountId?: string | null;
    cashAccountId?: string | null;
    bankAccountId?: string | null;
    foreignCurrencyAccountWarning?: string | null; // B4-0 (0-2), read-only
    taxPayableAccountId?: string | null;
    otherDeductionsPayableAccountId?: string | null;
    salariesPayableAccountId?: string | null;
    payslipEmailMode?: 'MANUAL' | 'AUTO_ON_FINALIZE';
    overtimePayEnabled?: boolean;
    overtimeMultiplier?: number;
    standardHoursPerDay?: number;
    /** S4-13: company daily OT cap in minutes; null = no cap. Send 0 to clear. */
    otDailyCapMinutes?: number | null;
    otHoursWarningEnabled?: boolean;
    attendanceBonusEnabled?: boolean;
    attendanceBonusAmount?: number | null;
    attendanceBonusMaxAbsences?: number;
    gratuityEnabled?: boolean;
    gratuityDaysPerServiceYear?: number | null;
    // T3b (piece-rate garments completion plan, 2026-09-09): opt-in toggle. Undefined/omitted on
    // save leaves the stored value alone (same rule as overtimePayEnabled above).
    pieceRateEnabled?: boolean;
    // Bangla payslip (2026-09-11): institution-level payslip render language. Omitted on save
    // leaves the stored value alone (same rule as the other opt-in flags above).
    payslipLanguage?: 'EN' | 'BN';
    // W3-D employee loans: always sent on save (the backend writes these unconditionally).
    employeeLoanReceivableAccountId?: string | null;
    employeeLoanWriteOffAccountId?: string | null;
    salaryAdvanceMaxPct?: number | null;
    clearLoanAccounts?: boolean;
}

// Festival bonus grant lifecycle (spec §2.1/§3).
export type FestivalBonusExclusionReason = 'NO_JOINING_DATE' | 'UNDER_ONE_YEAR' | 'NO_BASIC_COMPONENT' | 'NO_PIECE_WAGE_HISTORY';

export interface IFestivalBonusGrant {
    id: string;
    institutionId: string;
    occasion: string;
    targetYear: number;
    targetMonth: number;
    percentOfBasic: number;
    // Informational only (D12) — nothing on the backend gates delete on this; delete is refused
    // by whether payroll rows exist for the grant's month, not by whether it has been applied.
    appliedAt?: string | null;
    createdAt: string;
    // M-2: whether payroll rows already exist for this grant's (target year, target month) — the
    // same soft-delete-blind check the backend's delete endpoint refuses on. A grant can be
    // monthGenerated with appliedAt still null (the run had zero eligible employees); such a
    // grant can never be applied OR deleted.
    monthGenerated: boolean;
}

export interface ICreateFestivalBonusGrantRequest {
    occasion: string;
    targetYear: number;
    targetMonth: number;
    percentOfBasic: number;
}

export interface IFestivalBonusExcludedEmployee {
    userId: string;
    // Nit N-1: nullable to match the backend (FestivalBonusService.java — `user != null ?
    // user.getUsername() : null`), which is null when the user row is missing from usersByUser.
    username: string | null;
    employeeName?: string | null;
    reason: FestivalBonusExclusionReason;
}

export interface IFestivalBonusPreview {
    eligibleCount: number;
    excluded: IFestivalBonusExcludedEmployee[];
}

/** Result of "Finalize all": rows moved to FINALIZED by this call + per-row accrual warnings. */
export interface IFinalizeMonthResult {
    finalizedCount: number;
    accrualWarnings: { payrollId: string; message: string }[];
}

export interface IPayroll {
    id: string;
    institutionId: string;
    userId: string;
    userName: string;
    employeeName?: string;
    year: number;
    month: number;
    totalEarnings: number;
    totalDeductions: number;
    unpaidLeaveDays: number;
    unpaidLeaveDeduction: number;
    absentDays: number;
    absenceDeduction: number;
    grossSalary: number;
    netSalary: number;
    status: 'DRAFT' | 'FINALIZED';
    earningsBreakdown: Record<string, number>;
    deductionsBreakdown: Record<string, number>;
    segmentId?: string;
    payslipEmailedAt?: string | null;
    overtimeMinutes?: number;
    overtimeAmount?: number;
    festivalBonusAmount?: number;
    attendanceBonusAmount?: number;
    leaveEncashmentAmount?: number;
    expenseReimbursementAmount?: number;
    /** Calendar days employed in the period (null/absent = whole period). */
    payableDays?: number | null;
    periodDays?: number | null;
    /** Salary expense accrual at finalize: POSTED, NOT_POSTED (see accrualError), LEGACY (finalized before accruals existed); absent for drafts. */
    accrualStatus?: 'POSTED' | 'NOT_POSTED' | 'LEGACY' | null;
    accrualError?: string | null;
    accrualJournalEntryId?: string | null;
    /** Only on the finalize response when the accrual could not be posted. */
    accrualWarning?: string | null;
}

// Fix round 1 / H2 (task review of the 2026-09-08 decision note, D9): why an institution member
// was NOT given a payroll row on a generate run — mirrors the backend's ExcludedEmployeeDTO
// (payroll/dataclass/ExcludedEmployeeDTO.java).
export type PayrollExclusionReason =
    | 'MEMBERSHIP_SOFT_DELETED'
    | 'USER_DEACTIVATED'
    | 'USER_SOFT_DELETED'
    | 'EXITED_BEFORE_PERIOD'
    | 'PIECE_RATE_WITH_OVERTIME_ENABLED'
    | 'JOINED_AFTER_PERIOD'
    | 'EXIT_BEFORE_JOINING'
    | 'NO_ACTIVE_SALARY';

export interface IExcludedEmployee {
    userId: string;
    username: string | null;
    reason: PayrollExclusionReason;
    /** Filled for the no-active-salary list (board DA-03); absent on older responses. */
    fullName?: string | null;
    employeeCode?: string | null;
}

export interface INegativeNetSalaryWarning {
    userId: string;
    username: string | null;
    payrollId: string;
    netSalary: number;
}

// generatePayroll's response shape (GeneratePayrollResponse.java) — payrolls generated this run,
// every roster member dropped and why (D9/H7), and every generated payroll whose net salary came
// out non-positive (Audit Wave 2 Lane 4, M-135/C4/R6) — that payroll is still generated normally;
// it just cannot be PAID until corrected (SalaryPaymentService refuses the payment insert).
export interface IGeneratePayrollResponse {
    payrolls: IPayroll[];
    exclusions: IExcludedEmployee[];
    negativeNetSalaryWarnings: INegativeNetSalaryWarning[];
    /** W3-D: employees whose flat "Loan Repayment" component was replaced by loan-schedule recovery. */
    loanWarnings?: string[];
}

// Whole-run aggregate (SQL SUM over every live row), independent of the page being shown.
export interface IPayrollRunTotals {
    headcount: number;
    draftCount: number;
    finalizedCount: number;
    grossSalary: number;
    totalDeductions: number;
    unpaidLeaveDeduction: number;
    absenceDeduction: number;
    overtimeAmount: number;
    netSalary: number;
    // Board B-02/B-04: paid-of-payable picture of the FINALIZED rows (null/0 while the run is draft).
    paidAmount?: number;
    payableNetTotal?: number;
    payableCount?: number;
    paidCount?: number;
    paymentStatus?: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID' | null;
}

// B-21: maker-checker state of one payroll run.
export interface IPayrollRunApproval {
    state: 'NOT_APPROVED' | 'APPROVED' | 'REJECTED';
    actedBy: string | null;
    actedByName: string | null;
    actedAt: string | null;
    approvedNetTotal: number | null;
    approvedHeadcount: number | null;
    currentNetTotal: number;
    currentHeadcount: number;
    changedSinceApproval: boolean;
    reason: string | null;
    callerIsMaker: boolean;
    currentDraftCount: number;
}

// B-42: what changed in a payroll month compared with the month before (approver view).
export interface IPayrollVarianceFigures { net: number; gross: number; headcount: number }
export interface IPayrollVariancePerson { userId: string; name: string; employeeCode: string | null; net: number }
export interface IPayrollVariancePayoutChange {
    userId: string; name: string; employeeCode: string | null;
    kind: 'BANK' | 'WALLET' | 'METHOD'; previousMasked: string | null; currentMasked: string | null;
}
export interface IPayrollVarianceNetChange {
    userId: string; name: string; employeeCode: string | null;
    previousNet: number; currentNet: number; difference: number;
}
export interface IPayrollVarianceChange {
    kind: 'ROW_ADDED' | 'ROW_EDITED' | 'ROW_REMOVED' | 'EMPLOYEE_EDITED';
    userId: string | null; name: string | null; employeeCode: string | null;
    changedAt: string | null; changedBy: string | null; changedByName: string | null; aroundApprovalTime: boolean;
}
export interface IPayrollVarianceAfterApproval {
    approvedAt: string; approvedByName: string | null;
    approvedNet: number | null; approvedHeadcount: number | null;
    currentNet: number; currentHeadcount: number;
    changeCount: number; changes: IPayrollVarianceChange[]; historyNote: string;
}
// S4-06: payout warnings for the run (shared payout number, no payout method, paid by cheque). Information only.
export interface IPayrollVariancePersonRef { userId: string; name: string; employeeCode: string | null }
export interface IPayrollVarianceSharedNumber {
    kind: 'BANK' | 'WALLET'; maskedNumber: string; employeeCount: number; message: string;
    employees: IPayrollVariancePersonRef[];
}
export interface IPayrollVarianceNoPayout {
    userId: string; name: string; employeeCode: string | null; reason: string;
}
export interface IPayrollVariancePayoutChecks {
    sharedNumberGroupCount: number; sharedNumbers: IPayrollVarianceSharedNumber[];
    noPayoutMethodCount: number; noPayoutMethod: IPayrollVarianceNoPayout[];
    chequeCount?: number; chequeEmployees?: IPayrollVariancePersonRef[];
}
export interface IPayrollVariance {
    year: number; month: number; previousYear: number; previousMonth: number;
    current: IPayrollVarianceFigures;
    previous: IPayrollVarianceFigures | null;
    netDifference: number | null; grossDifference: number | null; headcountDifference: number | null;
    joinerCount: number; joiners: IPayrollVariancePerson[];
    leaverCount: number; leavers: IPayrollVariancePerson[];
    payoutChangeCount: number; payoutChanges: IPayrollVariancePayoutChange[];
    payoutComparedCount: number; payoutComparisonNote: string;
    biggestChanges: IPayrollVarianceNetChange[];
    afterApproval: IPayrollVarianceAfterApproval | null;
    payoutChecks?: IPayrollVariancePayoutChecks | null;
}

// B-41: a finalized payroll month still waiting for the approver.
export interface IPayrollPendingApproval {
    year: number;
    month: number;
    periodLabel: string;
    netTotal: number;
    headcount: number;
    state: 'NOT_APPROVED' | 'CHANGED';
    // The caller finalized or last edited this run, so maker-checker stops them approving it.
    callerIsMaker: boolean;
}

// B-44: may this batch's bank-advice / MFS file be downloaded?
export interface IPayrollBatchApproval {
    batchId: string;
    state: 'APPROVED' | 'NOT_APPROVED' | 'CHANGED' | 'BLOCKED' | 'NO_PAYROLL';
    periodLabel: string | null;
    exportable: boolean;
    reason: string | null;
}

// One FINALIZED row of a run, used to resolve "select all N in run" for bulk actions.
export interface IPayrollSelectionRow {
    id: string;
    employeeName: string;
    netSalary: number;
}

export interface IPayrollPageResponse {
    content: IPayroll[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

// Void-DRAFT-payroll-month response (decision note 2026-09-08, D1). FINALIZED rows are never
// touched by the void — a mixed month reports both counts.
export interface IVoidDraftPayrollResponse {
    voidedCount: number;
    finalizedSkippedCount: number;
}

// Leave encashment lifecycle (spec §2.1/§3). `appliedAt`/`amountPaid` are informational only —
// nothing on the backend gates on them. `monthGenerated` (V116 M-2 pattern) does NOT imply this
// row can no longer be deleted (unlike festival bonus) — delete is refused only when THIS
// encashment's `appliedAt` is set (spec §2.1/H4); DELETE visibility keys off `appliedAt` alone.
// The Applied/Pending BADGE, per spec §3 ("badges off appliedAt+monthGenerated"), is a separate
// three-state read of both fields (Fix round 1 F2): Applied, Pending, or "Pending — month
// generated" when the target month's payroll already ran without this row being applied.
export interface ILeaveEncashment {
    id: string;
    institutionId: string;
    userId: string;
    leaveTypeId: string;
    leaveTypeNameNormalized: string;
    days: number;
    targetYear: number;
    targetMonth: number;
    fiscalYear: number;
    amountPaid?: number | null;
    appliedAt?: string | null;
    createdAt: string;
    monthGenerated: boolean;
}

export interface ICreateLeaveEncashmentRequest {
    userId: string;
    leaveTypeId: string;
    days: number;
    targetYear: number;
    targetMonth: number;
}

// Single-tuple balance detail (spec §2.3), feeds the create-dialog's live remaining hint (§2.1
// H8 `/remaining` endpoint) — `remaining` is always `allocated - availed - encashedDays -
// settlementDays`.
export interface ILeaveBalanceDetail {
    allocated: number;
    availed: number;
    encashedDays: number;
    settlementDays: number;
    remaining: number;
    fiscalYear: number;
}

// Employee exit lifecycle (spec §2.4/§3). DISMISSAL (termination benefits REV 2 D3) is dismissal
// for misconduct under §23 -- distinct from TERMINATION (§26 termination simpliciter). Its service
// benefit can be explicitly forfeited at compute time via IComputeSettlementRequest.forfeitServiceBenefit.
export type ExitType =
    | 'RESIGNATION'
    | 'TERMINATION'
    | 'DISCHARGE'
    | 'RETRENCHMENT'
    | 'RETIREMENT'
    | 'DEATH'
    | 'ABSCONDING'
    | 'DISMISSAL';

export interface IEmployeeExit {
    id: string;
    institutionId: string;
    userId: string;
    // Null-safe the way every DTO in this slice resolves username: missing user row -> null,
    // frontend falls back to rendering userId.
    username: string | null;
    employeeName?: string | null;
    exitType: ExitType;
    lastWorkingDay: string;
    noticeDate?: string | null;
    remarks?: string | null;
    createdAt: string;
}

export interface ICreateEmployeeExitRequest {
    userId: string;
    exitType: ExitType;
    lastWorkingDay: string;
    noticeDate?: string | null;
    remarks?: string | null;
}

// Final settlement lifecycle (spec §2.5/§3).
export type SettlementStatus = 'DRAFT' | 'FINALIZED' | 'PAID' | 'VOIDED';

export interface IAdjustmentLine {
    label: string;
    amount: number;
}

// One persisted audit line of a settlement compute (spec §2.5 H6/H7). `code` is one of
// NO_ACTIVE_SALARY / NO_BASIC_COMPONENT / NO_JOINING_DATE / GRATUITY_DISABLED /
// NO_ENCASHABLE_TYPE / NO_ENCASHABLE_BALANCE (leaveTypeName/remaining null), or
// UNCONSUMED_ENCASHABLE_TYPE (leaveTypeName/remaining populated) — every active+paid+encashable
// type with a positive remaining balance that was NOT the one drawn.
export interface IComputationNote {
    code: string;
    leaveTypeName?: string | null;
    remaining?: number | null;
}

export interface IFinalSettlement {
    id: string;
    institutionId: string;
    userId: string;
    username: string | null;
    exitId: string;
    exitType: ExitType | null;
    lastWorkingDay: string | null;
    noticeDate?: string | null;
    status: SettlementStatus;
    leaveTypeId?: string | null;
    leaveTypeNameNormalized?: string | null;
    fiscalYear?: number | null;
    elDays: number;
    elAmount?: number | null;
    noticeDays: number;
    noticeAmount?: number | null;
    // Termination benefits REV 2 (D1/D5/R1): gratuityAmount is the gratuity CANDIDATE, not
    // necessarily what was paid. serviceBenefitAmount (whichever of gratuity or
    // statutoryCompensationAmount is higher, or compensation alone when gratuity is disabled) is
    // the amount actually paid and summed into totalAmount. Both, plus compensationDaysPerYear/
    // compensationYears, are null only on a pre-V123 legacy row -- render the legacy single
    // "Gratuity" row in that case.
    gratuityAmount?: number | null;
    serviceYears?: number | null;
    statutoryCompensationAmount?: number | null;
    serviceBenefitAmount?: number | null;
    compensationDaysPerYear?: number | null;
    compensationYears?: number | null;
    // No FK — informational only. Null when the payroll row for (user, lastWorkingDay's
    // year/month) cannot be resolved (spec §1 item 4 L2) — UI shows "payroll row unavailable".
    finalPayrollId?: string | null;
    adjustments: IAdjustmentLine[];
    computationNotes: IComputationNote[];
    totalAmount?: number | null;
    finalizedAt?: string | null;
    paidAt?: string | null;
    voidedAt?: string | null;
    createdAt: string;
}

export interface IComputeSettlementRequest {
    noticeDays?: number;
    adjustments?: IAdjustmentLine[];
    // Termination benefits REV 2 (R2): meaningful ONLY for ExitType 'DISMISSAL' -- zeroes both the
    // gratuity and statutory-compensation candidates for the §23 proviso cases (conviction /
    // specified grave misconduct) the system cannot adjudicate. Ignored (with a note) for any
    // other exit type. Defaults to false -- forfeiture is an explicit exception, never the default.
    forfeitServiceBenefit?: boolean;
}

// Employee self-service "My Payslips" (GET /payroll/me/...).
export interface IMyPayslipSummary {
    id: string;
    institutionId: string;
    year: number;
    month: number;
    grossSalary: number;
    netSalary: number;
    status: string;
}

export interface IMyPayslipLine {
    name: string;
    amount: number;
}

export interface IMyPayslipDetail {
    id: string;
    institutionId: string;
    year: number;
    month: number;
    status: string;
    earnings: IMyPayslipLine[];
    deductions: IMyPayslipLine[];
    totalEarnings: number;
    totalDeductions: number;
    grossSalary: number;
    netSalary: number;
    payableDays?: number | null;
    periodDays?: number | null;
    /** Attendance block (add-only); all come from the stored payroll row. */
    daysInMonth?: number | null;
    absentDays?: number | null;
    unpaidLeaveDays?: number | null;
    overtimeHours?: number | null;
    /** Average paid per overtime hour (amount / hours); null when there is no overtime. */
    overtimeRate?: number | null;
    overtimeAmount?: number | null;
}

/** H-1: an employee whose grade anniversary falls in the chosen year and who has not had this year's increment. */
export interface IDueForIncrement {
    userId: string;
    userName: string;
    wageGradeId: string;
    wageGradeDisplayName: string;
    joiningDate: string;
    anniversaryDate: string;
    currentBasic: number;
    incrementPercent: number;
    newBasic: number;
}

export type IncrementOutcome = 'APPLIED' | 'ALREADY_INCREMENTED' | 'NO_ACTIVE_SALARY' | 'NO_GRADE'
    | 'NO_INCREMENT_PERCENT' | 'EXISTING_ROW_AT_DATE' | 'NO_JOINING_DATE';

export interface IApplyIncrementRequest {
    institutionId: string;
    userIds: string[];
    /** yyyy-MM-dd, never in the future (the backend refuses the whole batch). */
    effectiveFrom: string;
}

export interface IApplyIncrementResult {
    userId: string;
    outcome: IncrementOutcome;
    newEmployeeSalaryId: string | null;
}
